import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

/**
 * Trigger for react-datepicker range mode — same visual language as DropdownButton
 * (see DropDown.jsx: border-[#d3d8df] / dark:border-[#263951], fills, label + chevron).
 */
const DateRangeDropdownTrigger = forwardRef(function DateRangeDropdownTrigger(props, ref) {
  const {
    value,
    onClick,
    onBlur,
    onFocus,
    onKeyDown,
    placeholder,
    disabled,
    className,
    id,
    name,
    autoFocus,
    required,
    title,
    tabIndex,
    form,
  } = props;

  const theme = useSelector((state) => state.theme.theme);
  const mergedClass = className ?? '';
  const isOpen = mergedClass.includes('react-datepicker-ignore-onclickoutside');

  const label = value || placeholder || 'Select Date Range';

  const surface =
    theme === 'light'
      ? `border-[#d3d8df] bg-[#FFFFFF] hover:bg-[#F5F5F5] ${
          isOpen ? 'border-[#7691CA] shadow-[0_0_4px_rgba(48,85,169,0.4)]' : ''
        }`
      : `border-[#263951] bg-[#182433] hover:bg-[#1E2B3A] ${
          isOpen ? 'border-[#066FD1]' : ''
        }`;

  return (
    <button
      type="button"
      ref={ref}
      id={id}
      name={name}
      title={title}
      form={form}
      disabled={disabled}
      autoFocus={autoFocus}
      required={required}
      tabIndex={tabIndex}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      className={`inline-flex w-full min-w-0 items-center justify-between gap-2 rounded border px-4 py-2 h-9 text-left text-sm transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[#066FD1] pr-8 dark:text-[#D9E4F1] text-[#333333] ${surface} ${mergedClass}`.trim()}
    >
      <span
        className={`truncate flex-1 min-w-0 text-sm ${!value ? 'opacity-80' : ''} dark:text-white text-[#24527A]`}
      >
        {label}
      </span>
      <svg
        className="w-2.5 h-2.5 flex-shrink-0 dark:text-white text-[#626262]"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 10 6"
      >
        <path
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="m1 1 4 4 4-4"
        />
      </svg>
    </button>
  );
});

DateRangeDropdownTrigger.displayName = 'DateRangeDropdownTrigger';

DateRangeDropdownTrigger.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  /** Injected by react-datepicker for text inputs; not used on this button trigger. */
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  autoFocus: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  title: PropTypes.string,
  tabIndex: PropTypes.number,
  autoComplete: PropTypes.string,
  form: PropTypes.string,
};

export default DateRangeDropdownTrigger;